import {
  AddUniversal,
  CloseUniversal,
  TickMarkOutlined,
  TimerUniversal,
  UndoUniversal,
} from '@duxton-web/icons';
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Empty,
  Flex,
  Input,
  Modal,
  Select,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  message,
} from '@duxton-web/ui';
import dayjs from 'dayjs';
import i18n from 'i18n-js';
import React, { useState } from 'react';

import {
  EVENT_DATE_FORMAT,
  formatDateTime,
  formatExpiry,
  localeSorter,
  nameFromEmail,
} from './adapters';
import DiffField from './DiffField';
import { editChangesText } from './editChanges';
import { contentConfigModalStyle } from './theme';

const { TextArea } = Input;
const { RangePicker } = DatePicker;

const eventText = (key, options = {}) =>
  i18n.t(`ucd_content_config.events.${key}`, options);

const statusTag = (status) => {
  if (status === 'pending') {
    return <Tag color="processing">{eventText('statuses.pending')}</Tag>;
  }
  return <Tag color="success">{eventText('statuses.active')}</Tag>;
};

interface FormFieldProps {
  children: React.ReactNode;
  className?: string;
  error?: React.ReactNode;
  help?: React.ReactNode;
  id: string;
  label: React.ReactNode;
  required?: boolean;
}

const FormField = ({
  children,
  className,
  error,
  help,
  id,
  label,
  required = false,
}: FormFieldProps) => (
  <div className={className ? `cc-field ${className}` : 'cc-field'}>
    <label className="cc-field-label" htmlFor={id}>
      <Typography.Body level={2} strong>
        {label}
        {required ? ' *' : ''}
      </Typography.Body>
    </label>
    {children}
    {(error || help) && (
      <Typography.Metadata
        id={`${id}-description`}
        type={error ? 'danger' : 'secondary'}
      >
        {error || help}
      </Typography.Metadata>
    )}
  </div>
);

export const EventList = ({
  canCreate,
  countries,
  countryFilter,
  listError,
  listTab,
  loading,
  onCreate,
  onOpenDetail,
  onRetry,
  rows,
  setCountryFilter,
  setListTab,
}) => {
  const columns = [
    {
      title: eventText('columns.name'),
      dataIndex: 'name',
      sorter: localeSorter('name'),
      width: 280,
      render: (name, row) => (
        <Flex align="center" gap={6}>
          <Typography.Body level={2}>{name}</Typography.Body>
          {row.status === 'pending' && (
            <Tooltip title={eventText('filters.pending')}>
              <TimerUniversal className="cc-pending-icon" />
            </Tooltip>
          )}
        </Flex>
      ),
    },
    {
      title: eventText('columns.country'),
      dataIndex: 'countryLabel',
      sorter: localeSorter('countryLabel'),
      width: 140,
    },
    {
      title: eventText('columns.start_date'),
      dataIndex: 'startDate',
      sorter: localeSorter('startDate'),
      width: 130,
    },
    {
      title: eventText('columns.end_date'),
      dataIndex: 'endDate',
      sorter: localeSorter('endDate'),
      width: 130,
    },
    {
      title: eventText('columns.status'),
      dataIndex: 'status',
      width: 160,
      render: statusTag,
    },
    {
      title: eventText('columns.updated_by'),
      dataIndex: 'updatedBy',
      width: 162,
      render: (user) => nameFromEmail(user) || '—',
    },
    {
      title: eventText('columns.updated_on'),
      dataIndex: 'updatedAt',
      width: 198,
      render: formatDateTime,
    },
  ];

  const countryOptions = [
    { value: '', label: eventText('filters.all_countries') },
    ...countries.map((country) => ({ value: country.id, label: country.name })),
  ];

  return (
    <Flex vertical gap="middle">
      <Flex align="center" gap="middle" wrap="wrap">
        <Typography.Title level={4} className="cc-title-reset">
          {eventText('title')}
        </Typography.Title>
        <Tooltip title={canCreate ? '' : eventText('permissions.create')}>
          <Button
            type="primary"
            icon={<AddUniversal />}
            disabled={!canCreate}
            onClick={onCreate}
          >
            {eventText('actions.create')}
          </Button>
        </Tooltip>
      </Flex>

      <Tabs
        type="pill"
        activeKey={listTab}
        onChange={setListTab}
        items={[
          { key: 'all', label: eventText('filters.all') },
          { key: 'pending', label: eventText('filters.pending') },
        ]}
      />

      <Flex align="center" gap={12} wrap="wrap" className="cc-list-controls">
        <label htmlFor="event-country-filter">
          <Typography.Body level={2} type="secondary">
            {eventText('filters.country')}
          </Typography.Body>
        </label>
        <Select
          id="event-country-filter"
          value={countryFilter}
          onChange={setCountryFilter}
          options={countryOptions}
          className="cc-country-select"
        />
      </Flex>

      {listError && (
        <Alert
          type="error"
          showIcon
          role="alert"
          message={(
            <Flex align="center" justify="space-between" gap={12} wrap="wrap">
              <span>{eventText('errors.list')}</span>
              <Button size="small" onClick={onRetry}>
                {eventText('actions.retry')}
              </Button>
            </Flex>
          )}
        />
      )}

      <Table
        rowKey="rowKey"
        columns={columns}
        dataSource={rows}
        loading={loading}
        pagination={false}
        scroll={{ x: 1200 }}
        onRow={(row) => ({
          className: 'cc-row-clickable',
          onClick: () => onOpenDetail(row.rowKey),
        })}
        locale={{
          emptyText: listTab === 'pending'
            ? eventText('empty.pending')
            : eventText('empty.all'),
        }}
      />
    </Flex>
  );
};

export const EventForm = ({
  countries,
  errors,
  form,
  onChange,
  onDateRangeChange,
  readOnly,
}) => {
  // The API carries YYYY-MM-DD strings; RangePicker wants Dayjs values. Only
  // build them when both ends are present so a half-filled draft still renders.
  const range = form.startDate && form.endDate
    ? [dayjs(form.startDate, EVENT_DATE_FORMAT), dayjs(form.endDate, EVENT_DATE_FORMAT)]
    : null;

  return (
    <Card bordered={false} className="cc-section-gap">
      <div className="cc-event-form-grid">
        <div>
          <FormField
            id="event-name"
            label={eventText('fields.name')}
            required
            error={errors.name}
          >
            <Input
              id="event-name"
              data-event-field="name"
              value={form.name}
              onChange={(event) => onChange('name', event.target.value)}
              disabled={readOnly}
              placeholder={eventText('placeholders.name')}
              aria-required
              aria-invalid={Boolean(errors.name)}
              aria-describedby={errors.name ? 'event-name-description' : undefined}
            />
          </FormField>

          <FormField
            id="event-country"
            label={eventText('fields.country')}
            required
            error={errors.country}
            help={form.isDraft ? undefined : eventText('help.country_locked')}
          >
            <Select
              id="event-country"
              data-event-field="country"
              value={form.country || undefined}
              onChange={(value) => onChange('country', value)}
              options={countries.map((country) => ({
                value: country.id,
                label: country.name,
              }))}
              optionFilterProp="label"
              showSearch
              // The backend copies the base row's country on update, so it is
              // only selectable while drafting.
              disabled={readOnly || !form.isDraft}
              className="cc-full-width"
              aria-required
              aria-invalid={Boolean(errors.country)}
              aria-describedby="event-country-description"
            />
          </FormField>

          <FormField
            id="event-date-range"
            label={eventText('fields.date_range')}
            required
            error={errors.dateRange}
            help={eventText('help.date_range')}
          >
            <RangePicker
              id="event-date-range"
              data-event-field="dateRange"
              value={range}
              format={EVENT_DATE_FORMAT}
              onChange={(dates) => onDateRangeChange(
                dates && dates[0] ? dates[0].format(EVENT_DATE_FORMAT) : '',
                dates && dates[1] ? dates[1].format(EVENT_DATE_FORMAT) : '',
              )}
              disabled={readOnly}
              allowClear
              className="cc-full-width"
              aria-required
              aria-invalid={Boolean(errors.dateRange)}
              aria-describedby="event-date-range-description"
            />
          </FormField>
        </div>

        <div>
          <FormField
            className="cc-event-description"
            id="event-description"
            label={eventText('fields.description')}
            required
            error={errors.description}
          >
            <TextArea
              id="event-description"
              data-event-field="description"
              value={form.description}
              onChange={(event) => onChange('description', event.target.value)}
              disabled={readOnly}
              placeholder={eventText('placeholders.description')}
              aria-required
              aria-invalid={Boolean(errors.description)}
              aria-describedby={
                errors.description ? 'event-description-description' : undefined
              }
            />
          </FormField>
        </div>
      </div>
    </Card>
  );
};

export const EventDiffModal = ({
  active,
  busy,
  canRecall,
  canReview,
  countryLabel,
  onApprove,
  onCancel,
  onEditChanges,
  onRecall,
  onReject,
  open,
  pending,
}) => {
  const onRequestReject = () => {
    let comment = '';
    Modal.confirm({
      title: eventText('messages.reject_title'),
      content: (
        <Input.TextArea
          placeholder={eventText('placeholders.rejection_comment')}
          onChange={(event) => { comment = event.target.value; }}
        />
      ),
      okText: eventText('actions.reject'),
      okType: 'danger',
      onOk: () => {
        if (!comment.trim()) {
          message.error(eventText('validation.rejection_comment'));
          return Promise.reject();
        }
        onReject(comment);
        return Promise.resolve();
      },
    });
  };

  return (
    <Modal
      title={eventText('diff.title')}
      open={open}
      onCancel={onCancel}
      footer={(
        <Flex justify="flex-end" gap={8}>
          {canRecall && (
            <React.Fragment>
              <Button size="small" icon={<UndoUniversal />} danger loading={busy} onClick={onRecall}>
                {eventText('actions.recall')}
              </Button>
              <Button size="small" loading={busy} onClick={onEditChanges}>
                {editChangesText('action')}
              </Button>
            </React.Fragment>
          )}
          {canReview && (
            <React.Fragment>
              <Button
                size="small"
                type="primary"
                danger
                icon={<CloseUniversal />}
                loading={busy}
                onClick={onRequestReject}
              >
                {eventText('actions.reject')}
              </Button>
              <Button
                size="small"
                type="primary"
                icon={<TickMarkOutlined />}
                loading={busy}
                onClick={onApprove}
              >
                {eventText('actions.approve')}
              </Button>
            </React.Fragment>
          )}
        </Flex>
      )}
      width={1472}
      style={contentConfigModalStyle}
      className="cc-modal"
    >
      {active && pending ? (
        <div className="cc-diff">
          <DiffField
            label={eventText('fields.name')}
            activeVal={active.name}
            pendingVal={pending.name}
          />
          <DiffField
            label={eventText('fields.country')}
            activeVal={countryLabel(active.country)}
            pendingVal={countryLabel(pending.country)}
          />
          <DiffField
            label={eventText('columns.start_date')}
            activeVal={active.startDate}
            pendingVal={pending.startDate}
          />
          <DiffField
            label={eventText('columns.end_date')}
            activeVal={active.endDate}
            pendingVal={pending.endDate}
          />
          <DiffField
            label={eventText('fields.description')}
            activeVal={active.description}
            pendingVal={pending.description}
          />
        </div>
      ) : null}
    </Modal>
  );
};

export const EventDetail = ({
  active,
  busy,
  canRecall,
  canReview,
  countries,
  errors,
  form,
  hasChanges,
  isDraft,
  isEditing,
  onApprove,
  onCancelEdit,
  onChange,
  onDateRangeChange,
  onEditChanges,
  onRecall,
  onReject,
  onRequestReject,
  onSubmit,
  pending,
  record,
  setShowPending,
  showPending,
}) => {
  const [showDiff, setShowDiff] = useState(false);
  const countryLabel = (country) => (
    (countries.find((item) => item.id === country) || {}).name || country || '—'
  );
  const hasPending = Boolean(pending);
  const pendingAuthor = hasPending
    ? pending.updatedBy || pending.createdBy
    : '';
  const pendingExpiry = hasPending ? formatExpiry(pending.expiresAt) : '';
  const showingPending = hasPending && (!active || showPending);

  if (!record && !isDraft) {
    return <Empty description={eventText('empty.selection')} />;
  }

  return (
    <Flex vertical gap="middle">
      {hasPending && !isEditing && (
        <Alert
          type="info"
          showIcon
          className="cc-action-bar"
          message={(
            <Flex justify="space-between" align="center" gap={12} wrap="wrap">
              <div className="cc-alert-content">
                <div>
                  {eventText('messages.showing_version_prefix')}
                  {' '}
                  <strong>
                    {showingPending
                      ? eventText('statuses.pending')
                      : eventText('statuses.active')}
                  </strong>
                  {' '}
                  {eventText('messages.pending_locked')}
                </div>
                <div>
                  {eventText('messages.pending_author')}
                  {' '}
                  <strong>{pendingAuthor || '—'}</strong>
                  {pendingExpiry ? (
                    <React.Fragment>
                      {' '}
                      {eventText('messages.pending_expiry')}
                      {' '}
                      <strong>{pendingExpiry}</strong>
                    </React.Fragment>
                  ) : null}
                  .
                </div>
              </div>
              {active && (
                <Button size="small" onClick={() => setShowPending(!showPending)}>
                  {showPending
                    ? eventText('actions.see_active')
                    : eventText('actions.see_changes')}
                </Button>
              )}
            </Flex>
          )}
        />
      )}

      <Flex vertical gap={4} className="cc-detail-header">
        <Typography.Title level={4} className="cc-title-reset">
          {(isEditing ? form.name : record && record.name)
            || eventText('new_title')}
        </Typography.Title>
        {!isDraft && record && (
          <Typography.Metadata type="secondary">
            {eventText('messages.updated_by', {
              user: nameFromEmail(record.updatedBy || record.createdBy) || '—',
              date: formatDateTime(record.updatedAt),
            })}
          </Typography.Metadata>
        )}
      </Flex>

      <Flex
        align="center"
        justify="space-between"
        wrap="wrap"
        gap={12}
        className="cc-action-bar"
      >
        {isDraft ? (
          <Tag color="warning">{eventText('statuses.draft')}</Tag>
        ) : statusTag(hasPending ? 'pending' : 'active')}
        <Flex gap={8} align="center" wrap="wrap">
          {hasPending && active && (
            <Button size="small" onClick={() => setShowDiff(true)}>
              {eventText('actions.show_diff')}
            </Button>
          )}
          {hasPending && canRecall && (
            <React.Fragment>
              <Button size="small" icon={<UndoUniversal />} danger loading={busy} onClick={onRecall}>
                {eventText('actions.recall')}
              </Button>
              <Button size="small" loading={busy} onClick={onEditChanges}>
                {editChangesText('action')}
              </Button>
            </React.Fragment>
          )}
          {hasPending && canReview && (
            <React.Fragment>
              <Button
                size="small"
                type="primary"
                danger
                icon={<CloseUniversal />}
                loading={busy}
                onClick={onRequestReject}
              >
                {eventText('actions.reject')}
              </Button>
              <Button
                size="small"
                type="primary"
                icon={<TickMarkOutlined />}
                loading={busy}
                onClick={onApprove}
              >
                {eventText('actions.approve')}
              </Button>
            </React.Fragment>
          )}
          {isDraft && (
            <Button
              size="small"
              icon={<UndoUniversal />}
              danger
              disabled={busy}
              onClick={onCancelEdit}
            >
              {eventText('actions.discard')}
            </Button>
          )}
          {!isDraft && isEditing && hasChanges && (
            <Button
              size="small"
              icon={<UndoUniversal />}
              danger
              disabled={busy}
              onClick={onCancelEdit}
            >
              {eventText('actions.discard_local_edits')}
            </Button>
          )}
          <Button
            size="small"
            type="primary"
            icon={<TickMarkOutlined />}
            loading={busy}
            disabled={!isEditing || (!isDraft && !hasChanges)}
            onClick={onSubmit}
          >
            {eventText('actions.send_for_approval')}
          </Button>
        </Flex>
      </Flex>

      <EventForm
        countries={countries}
        errors={isEditing ? errors : {}}
        form={isEditing ? form : { ...record, isDraft: false }}
        onChange={onChange}
        onDateRangeChange={onDateRangeChange}
        readOnly={!isEditing}
      />

      {pending && (
        <EventDiffModal
          active={active}
          busy={busy}
          canRecall={canRecall}
          canReview={canReview}
          countryLabel={countryLabel}
          onApprove={() => {
            setShowDiff(false);
            onApprove();
          }}
          onCancel={() => setShowDiff(false)}
          onEditChanges={() => {
            setShowDiff(false);
            onEditChanges();
          }}
          onRecall={() => {
            setShowDiff(false);
            onRecall();
          }}
          onReject={(comment) => {
            setShowDiff(false);
            onReject(comment);
          }}
          open={showDiff}
          pending={pending}
        />
      )}
    </Flex>
  );
};
